'use client';

import { c as _c } from "react/compiler-runtime";
import { SpaceMemberRoles } from '@shared/all/domains/spaces';
import toast from '@shared/utils/helpers/toast';
import useSpace from '@shared/utils/hooks/useSpace';
import useUser from '@shared/utils/hooks/useUser';
import { useQueryClient } from '@tanstack/react-query';
import { MoreVertical } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useConfirmIfPrivate } from '../../hooks/useConfirmIfPrivate';
import { client, type RouterOutputs } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import BanUserConfirmation from '../common/BanUserConfirmation';
import Button from '../common/Button';
import Confirmation from '../common/Confirmation';
import ScrollableText from '../common/ScrollableText';
import TransferCardsModal from '../spaces/TransferCardsModal';
export type ListUser = NonNullable<RouterOutputs['users']['mutedUsers']['users'][number]>;

// Anchored to the ⋮ button: below it (`top`) or, flipped, above it (`bottom`).
type MenuPos = {
  top?: number;
  bottom?: number;
  right: number;
  maxHeight: number;
};
// Space between the button and the menu.
const MENU_GAP = 8;
// Kept clear between the menu and the viewport edge.
const MENU_EDGE = 8;
// The tallest the menu gets (five 40px rows plus padding); with less room than
// this below the button the menu may flip.
const MENU_FULL_HEIGHT = 216;
type Props = {
  showAuthModal?: () => void;
  loggedIn?: boolean | null;
  viewOnly?: boolean;
  title?: string;
  isSpace?: boolean;
  isInvited?: boolean;
  invitedByName?: string | null;
  invitedEmail?: string | null;
  user: ListUser;
  space?: RouterOutputs['spaces']['get'];
};
const InlineProfileCard = props => {
  const $ = _c(143);
  const router = useRouter();
  const {
    currentUser
  } = useUser();
  const {
    isOwner,
    isModerator,
    updateSpace
  } = useSpace();
  const queryClient = useQueryClient();
  const [confirmModal, setConfirmModal] = useState(null);
  const t0 = props.title === "Muted Users";
  const t1 = props.title === "Blocked Users";
  let t2;
  if ($[0] !== props.user.following || $[1] !== t0 || $[2] !== t1) {
    t2 = {
      following: props.user.following,
      muted: t0,
      blocked: t1
    };
    $[0] = props.user.following;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [relation, setRelation] = useState(t2);
  const [menuPos, setMenuPos] = useState(null);
  const dropdownOpen = menuPos !== null;
  let t3;
  if ($[4] !== dropdownOpen) {
    t3 = e => {
      e.stopPropagation();
      if (dropdownOpen) {
        return setMenuPos(null);
      }
      const rect = e.currentTarget.getBoundingClientRect();
      const right = window.innerWidth - rect.right;
      const below = window.innerHeight - rect.bottom - MENU_GAP - MENU_EDGE;
      const above = rect.top - MENU_GAP - MENU_EDGE;
      if (below < MENU_FULL_HEIGHT && above > below) {
        return setMenuPos({
          bottom: window.innerHeight - rect.top + MENU_GAP,
          right,
          maxHeight: above
        });
      }
      setMenuPos({
        top: rect.bottom + MENU_GAP,
        right,
        maxHeight: below
      });
    };
    $[4] = dropdownOpen;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const openDropdown = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setMenuPos(null);
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const closeDropdown = t4;
  const {
    ensureConfirmed,
    dialog: privateConfirmDialog
  } = useConfirmIfPrivate();
  let t5;
  if ($[7] !== ensureConfirmed || $[8] !== props || $[9] !== queryClient) {
    t5 = async type => {
      if (!props.loggedIn && props.showAuthModal) {
        return props.showAuthModal();
      }
      if (type === "follow") {
        if (!(await ensureConfirmed("follow"))) {
          return;
        }
        const [error] = await client.users.followUser.mutate({
          username: props.user.username
        });
        if (error) {
          return;
        }
        setRelation(_temp);
      } else {
        const [error_0] = await client.users.unfollowUser.mutate({
          username: props.user.username
        });
        if (error_0) {
          return;
        }
        setRelation(_temp2);
      }
      queryClient.invalidateQueries({
        queryKey: ["users", "followCounts"]
      });
    };
    $[7] = ensureConfirmed;
    $[8] = props;
    $[9] = queryClient;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const followButtonHandler = t5;
  let t6;
  if ($[11] !== props.user.username || $[12] !== relation.muted) {
    t6 = async () => {
      const next = !relation.muted;
      const [error_1] = await (next ? client.users.muteUser.mutate({
        username: props.user.username
      }) : client.users.unmuteUser.mutate({
        username: props.user.username
      }));
      if (error_1) {
        return;
      }
      setRelation(r_1 => ({
        ...r_1,
        muted: next
      }));
    };
    $[11] = props.user.username;
    $[12] = relation.muted;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const muteButtonClickHandler = t6;
  let t7;
  if ($[14] !== props.user.username || $[15] !== relation.blocked) {
    t7 = async () => {
      const next_0 = !relation.blocked;
      const [error_2] = await (next_0 ? client.users.blockUser.mutate({
        username: props.user.username
      }) : client.users.unblockUser.mutate({
        username: props.user.username
      }));
      if (error_2) {
        return;
      }
      setRelation(r_2 => ({
        ...r_2,
        blocked: next_0
      }));
    };
    $[14] = props.user.username;
    $[15] = relation.blocked;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const blockButtonClickHandler = t7;
  let t8;
  if ($[17] !== currentUser.id || $[18] !== props.space || $[19] !== props.user.username || $[20] !== updateSpace) {
    t8 = async () => {
      if (!props.space) {
        return;
      }
      const [error_3] = await client.spaces.transferOwnership.mutate({
        slug: props.space.spaceSlug,
        username: props.user.username
      });
      if (error_3) {
        return;
      }
      updateSpace({
        ...props.space,
        owners: props.space.owners.filter(owner => owner.id !== currentUser.id),
        membership: props.space.membership && {
          ...props.space.membership,
          roles: [SpaceMemberRoles.MEMBER]
        }
      });
      setConfirmModal(null);
    };
    $[17] = currentUser.id;
    $[18] = props.space;
    $[19] = props.user.username;
    $[20] = updateSpace;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleOwnershipTransfer = t8;
  let t9;
  if ($[22] !== props.space || $[23] !== props.user.username) {
    t9 = async ruleId => {
      if (!props.space) {
        return;
      }
      const [error_4] = await client.spaces.banUser.mutate({
        slug: props.space.spaceSlug,
        username: props.user.username,
        ruleId
      });
      if (error_4) {
        return;
      }
      setConfirmModal(null);
    };
    $[22] = props.space;
    $[23] = props.user.username;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const handleBanMember = t9;
  let t10;
  if ($[25] !== props.invitedEmail || $[26] !== props.space) {
    t10 = async () => {
      if (!props.space || !props.invitedEmail) {
        return;
      }
      const [error_5] = await client.spaces.inviteByEmail.mutate({
        slug: props.space.spaceSlug,
        email: props.invitedEmail
      });
      if (error_5) {
        toast(error_5.message ?? "Failed to resend invite.", "", "error");
        return;
      }
      toast(`Invite email sent to ${props.invitedEmail}`);
    };
    $[25] = props.invitedEmail;
    $[26] = props.space;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const handleResendInviteEmail = t10;
  let t11;
  if ($[28] !== props.space || $[29] !== props.user.id || $[30] !== queryClient) {
    t11 = async () => {
      if (!props.space) {
        return;
      }
      const [error_6] = await client.spaces.cancelInvite.mutate({
        slug: props.space.spaceSlug,
        inviteUserId: props.user.id
      });
      if (error_6) {
        return;
      }
      const slug = props.space.spaceSlug;
      const targetId = props.user.id;
      queryClient.setQueriesData({
        queryKey: ["SpaceMembersList", slug]
      }, old => {
        if (!old) {
          return old;
        }
        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            users: page.users.filter(u => u.id !== targetId)
          }))
        };
      });
      queryClient.invalidateQueries({
        queryKey: ["SpaceMembersList", slug]
      });
      setConfirmModal(null);
    };
    $[28] = props.space;
    $[29] = props.user.id;
    $[30] = queryClient;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const handleRevokeInvite = t11;
  let t12;
  if ($[32] !== props.space || $[33] !== props.user.id || $[34] !== props.user.username || $[35] !== queryClient) {
    t12 = async () => {
      if (!props.space) {
        return;
      }
      const [error_7] = await client.spaces.removeMember.mutate({
        slug: props.space.spaceSlug,
        username: props.user.username
      });
      if (error_7) {
        return;
      }
      const slug_0 = props.space.spaceSlug;
      const targetId_0 = props.user.id;
      queryClient.setQueriesData({
        queryKey: ["SpaceMembersList", slug_0]
      }, old_0 => {
        if (!old_0) {
          return old_0;
        }
        return {
          ...old_0,
          pages: old_0.pages.map(page_0 => ({
            ...page_0,
            users: page_0.users.filter(u_0 => u_0.id !== targetId_0)
          }))
        };
      });
      queryClient.invalidateQueries({
        queryKey: ["SpaceMembersList", slug_0]
      });
      setConfirmModal(null);
    };
    $[32] = props.space;
    $[33] = props.user.id;
    $[34] = props.user.username;
    $[35] = queryClient;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const handleRemoveMember = t12;
  let t13;
  if ($[37] !== isModerator || $[38] !== isOwner || $[39] !== props.space) {
    t13 = props.space && (isOwner(props.space) || isModerator(props.space));
    $[37] = isModerator;
    $[38] = isOwner;
    $[39] = props.space;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const viewerIsMod = Boolean(t13);
  const canModerate = viewerIsMod && props.user.id !== currentUser.id;
  let t14;
  if ($[41] !== props.space?.owners || $[42] !== props.user.id) {
    t14 = props.space?.owners.some(owner_0 => owner_0.id === props.user.id);
    $[41] = props.space?.owners;
    $[42] = props.user.id;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  const targetIsOwner = Boolean(t14);
  let t15;
  if ($[44] !== blockButtonClickHandler || $[45] !== canModerate || $[46] !== currentUser.id || $[47] !== followButtonHandler || $[48] !== handleResendInviteEmail || $[49] !== isOwner || $[50] !== muteButtonClickHandler || $[51] !== props.invitedEmail || $[52] !== props.isInvited || $[53] !== props.space || $[54] !== props.title || $[55] !== props.user.id || $[56] !== props.viewOnly || $[57] !== relation.blocked || $[58] !== relation.following || $[59] !== relation.muted || $[60] !== targetIsOwner || $[61] !== viewerIsMod) {
    t15 = () => <>{props.space && isOwner(props.space) && props.user.id !== currentUser.id && <button className="w-full rounded-md px-4 py-2 text-left hover:bg-background-light" onClick={() => {
        closeDropdown();
        setConfirmModal("transfer");
      }}>Transfer Ownership</button>}{canModerate && props.isInvited && props.invitedEmail && <button className="w-full rounded-md px-4 py-2 text-left hover:bg-background-light" onClick={() => {
        closeDropdown();
        handleResendInviteEmail();
      }}>Resend Email</button>}{canModerate && props.isInvited && <button className="w-full rounded-md px-4 py-2 text-left hover:bg-background-light" onClick={() => {
        closeDropdown();
        setConfirmModal("revokeInvite");
      }}>Revoke Invite</button>}{viewerIsMod && !props.isInvited && props.space && <button className="w-full rounded-md px-4 py-2 text-left hover:bg-background-light" onClick={() => {
        closeDropdown();
        setConfirmModal("transferCards");
      }}>Transfer cards…</button>}{canModerate && !props.isInvited && !targetIsOwner && <button className="w-full rounded-md px-4 py-2 text-left hover:bg-background-light" onClick={() => {
        closeDropdown();
        setConfirmModal("remove");
      }}>Remove from Space</button>}{canModerate && !props.isInvited && !targetIsOwner && <button className="w-full rounded-md px-4 py-2 text-left hover:bg-background-light" onClick={() => {
        closeDropdown();
        setConfirmModal("ban");
      }}>Ban Member</button>}{relation.following !== null && !props.viewOnly && props.title !== "Blocked Users" && props.title !== "Muted Users" && <button className="w-full rounded-md px-4 py-2 text-left hover:bg-background-light" onClick={() => {
        closeDropdown();
        followButtonHandler(relation.following ? "unfollow" : "follow");
      }}>{relation.following ? "Unfollow" : "Follow"}</button>}{props.title === "Blocked Users" && <button className="w-full rounded-md px-4 py-2 text-left hover:bg-background-light" onClick={() => {
        closeDropdown();
        blockButtonClickHandler();
      }}>{relation.blocked ? "Unblock" : "Block"}</button>}{props.title === "Muted Users" && <button className="w-full rounded-md px-4 py-2 text-left hover:bg-background-light" onClick={() => {
        closeDropdown();
        muteButtonClickHandler();
      }}>{relation.muted ? "Unmute" : "Mute"}</button>}</>;
    $[44] = blockButtonClickHandler;
    $[45] = canModerate;
    $[46] = currentUser.id;
    $[47] = followButtonHandler;
    $[48] = handleResendInviteEmail;
    $[49] = isOwner;
    $[50] = muteButtonClickHandler;
    $[51] = props.invitedEmail;
    $[52] = props.isInvited;
    $[53] = props.space;
    $[54] = props.title;
    $[55] = props.user.id;
    $[56] = props.viewOnly;
    $[57] = relation.blocked;
    $[58] = relation.following;
    $[59] = relation.muted;
    $[60] = targetIsOwner;
    $[61] = viewerIsMod;
    $[62] = t15;
  } else {
    t15 = $[62];
  }
  const renderButtons = t15;
  const t16 = `flex min-w-0 flex-1 items-center ${props.invitedEmail ? "" : "cursor-pointer"}`;
  let t17;
  if ($[63] !== props.invitedEmail || $[64] !== props.user.username || $[65] !== router) {
    t17 = () => {
      if (!props.invitedEmail) {
        router.push(`/user/${props.user.username}`);
      }
    };
    $[63] = props.invitedEmail;
    $[64] = props.user.username;
    $[65] = router;
    $[66] = t17;
  } else {
    t17 = $[66];
  }
  const t18 = props.user.tag === "BOT";
  let t19;
  if ($[67] !== props.user.avatarKey || $[68] !== props.user.id || $[69] !== props.user.robohash || $[70] !== t18) {
    t19 = <Avatar avatarKey={props.user.avatarKey} robohash={props.user.robohash} isBot={t18} size={40} className="shrink-0 rounded-full object-cover" userId={props.user.id} />;
    $[67] = props.user.avatarKey;
    $[68] = props.user.id;
    $[69] = props.user.robohash;
    $[70] = t18;
    $[71] = t19;
  } else {
    t19 = $[71];
  }
  const t20 = props.invitedEmail || props.user.name || `@${props.user.username}`;
  let t21;
  if ($[72] !== t20) {
    t21 = <ScrollableText className="font-emphasis">{t20}</ScrollableText>;
    $[72] = t20;
    $[73] = t21;
  } else {
    t21 = $[73];
  }
  let t22;
  if ($[74] !== props.invitedByName || $[75] !== props.isInvited) {
    t22 = props.isInvited && <span className="shrink-0 rounded-full bg-yellow-500/20 px-2 py-0.5 text-xs text-yellow-600">Invited{props.invitedByName ? ` by ${props.invitedByName}` : ""}</span>;
    $[74] = props.invitedByName;
    $[75] = props.isInvited;
    $[76] = t22;
  } else {
    t22 = $[76];
  }
  let t23;
  if ($[77] !== t21 || $[78] !== t22) {
    t23 = <div className="flex items-center gap-2">{t21}{t22}</div>;
    $[77] = t21;
    $[78] = t22;
    $[79] = t23;
  } else {
    t23 = $[79];
  }
  let t24;
  if ($[80] !== props.invitedEmail || $[81] !== props.user.name || $[82] !== props.user.username) {
    t24 = !props.invitedEmail && props.user.name && <ScrollableText className="text-sm text-textSecondary/70">@{props.user.username}</ScrollableText>;
    $[80] = props.invitedEmail;
    $[81] = props.user.name;
    $[82] = props.user.username;
    $[83] = t24;
  } else {
    t24 = $[83];
  }
  const t25 = props.invitedEmail ? "Email invite pending" : props.user.bio || "No bio provided";
  let t26;
  if ($[84] !== t25) {
    t26 = <ScrollableText className="text-base text-textSecondary/70">{t25}</ScrollableText>;
    $[84] = t25;
    $[85] = t26;
  } else {
    t26 = $[85];
  }
  let t27;
  if ($[86] !== t23 || $[87] !== t24 || $[88] !== t26) {
    t27 = <div className="ml-3 min-w-0 flex-1 text-start">{t23}{t24}{t26}</div>;
    $[86] = t23;
    $[87] = t24;
    $[88] = t26;
    $[89] = t27;
  } else {
    t27 = $[89];
  }
  let t28;
  if ($[90] !== t16 || $[91] !== t17 || $[92] !== t19 || $[93] !== t27) {
    t28 = <div className={t16} onClick={t17}>{t19}{t27}</div>;
    $[90] = t16;
    $[91] = t17;
    $[92] = t19;
    $[93] = t27;
    $[94] = t28;
  } else {
    t28 = $[94];
  }
  let t29;
  if ($[95] !== blockButtonClickHandler || $[96] !== currentUser.id || $[97] !== currentUser.isAuthenticated || $[98] !== menuPos || $[99] !== muteButtonClickHandler || $[100] !== openDropdown || $[101] !== props.title || $[102] !== props.user.id || $[103] !== relation.blocked || $[104] !== relation.muted || $[105] !== renderButtons || $[106] !== viewerIsMod) {
    t29 = currentUser.isAuthenticated && <div className="flex items-center space-x-2">{props.title === "Muted Users" && <Button text={relation.muted ? "Unmute" : "Mute"} variant="PRIMARY" classNames="text-sm px-4 py-1" onClick={muteButtonClickHandler} />}{props.title === "Blocked Users" && <Button text={relation.blocked ? "Unblock" : "Block"} variant="PRIMARY" classNames="text-sm px-4 py-1" onClick={blockButtonClickHandler} />}{props.title !== "Muted Users" && props.title !== "Blocked Users" && (props.user.id !== currentUser.id || viewerIsMod) && <><button onClick={openDropdown} className="rounded p-1 hover:bg-background-light"><MoreVertical /></button>{menuPos && typeof document !== "undefined" && createPortal(<><div className="fixed inset-0 z-popover" onClick={closeDropdown} /><div style={{
            position: "fixed",
            top: menuPos.top,
            bottom: menuPos.bottom,
            right: menuPos.right,
            maxHeight: menuPos.maxHeight
          }} className="z-popover w-48 overflow-y-auto rounded-md border border-ds-border-standard bg-background-secondary shadow-lg"><ul className="py-1">{renderButtons()}</ul></div></>, document.body)}</>}</div>;
    $[95] = blockButtonClickHandler;
    $[96] = currentUser.id;
    $[97] = currentUser.isAuthenticated;
    $[98] = menuPos;
    $[99] = muteButtonClickHandler;
    $[100] = openDropdown;
    $[101] = props.title;
    $[102] = props.user.id;
    $[103] = relation.blocked;
    $[104] = relation.muted;
    $[105] = renderButtons;
    $[106] = viewerIsMod;
    $[107] = t29;
  } else {
    t29 = $[107];
  }
  let t30;
  if ($[108] !== confirmModal || $[109] !== handleOwnershipTransfer || $[110] !== props.user.username) {
    t30 = confirmModal === "transfer" && <Confirmation title="Are you sure?" message={`You won't be the owner of this space anymore and ownership will instead be transferred to ${props.user.username}`} cancelHandler={() => setConfirmModal(null)} confirmHandler={handleOwnershipTransfer} actionButtonText="Transfer Ownership" />;
    $[108] = confirmModal;
    $[109] = handleOwnershipTransfer;
    $[110] = props.user.username;
    $[111] = t30;
  } else {
    t30 = $[111];
  }
  let t31;
  if ($[112] !== confirmModal || $[113] !== handleBanMember || $[114] !== props.space) {
    t31 = confirmModal === "ban" && props.space && <BanUserConfirmation closeHandler={() => setConfirmModal(null)} confirmHandler={handleBanMember} spaceSlug={props.space.spaceSlug} />;
    $[112] = confirmModal;
    $[113] = handleBanMember;
    $[114] = props.space;
    $[115] = t31;
  } else {
    t31 = $[115];
  }
  let t32;
  if ($[116] !== confirmModal || $[117] !== handleRemoveMember || $[118] !== props.space || $[119] !== props.user.username) {
    t32 = confirmModal === "remove" && props.space && <Confirmation title="Remove from Space?" message={`@${props.user.username} will be removed from this space. They can be invited back later.`} cancelHandler={() => setConfirmModal(null)} confirmHandler={handleRemoveMember} actionButtonText="Remove" />;
    $[116] = confirmModal;
    $[117] = handleRemoveMember;
    $[118] = props.space;
    $[119] = props.user.username;
    $[120] = t32;
  } else {
    t32 = $[120];
  }
  let t33;
  if ($[121] !== confirmModal || $[122] !== handleRevokeInvite || $[123] !== props.space || $[124] !== props.user.username) {
    t33 = confirmModal === "revokeInvite" && props.space && <Confirmation title="Revoke Invite?" message={`The invitation to @${props.user.username} will be cancelled. You can invite them again later.`} cancelHandler={() => setConfirmModal(null)} confirmHandler={handleRevokeInvite} actionButtonText="Revoke Invite" />;
    $[121] = confirmModal;
    $[122] = handleRevokeInvite;
    $[123] = props.space;
    $[124] = props.user.username;
    $[125] = t33;
  } else {
    t33 = $[125];
  }
  let t34;
  if ($[126] !== confirmModal || $[127] !== props.space || $[128] !== props.user.id || $[129] !== props.user.name || $[130] !== props.user.username) {
    t34 = confirmModal === "transferCards" && props.space && <TransferCardsModal spaceSlug={props.space.spaceSlug} fromUser={{
      id: props.user.id,
      username: props.user.username,
      name: props.user.name
    }} closeHandler={() => setConfirmModal(null)} />;
    $[126] = confirmModal;
    $[127] = props.space;
    $[128] = props.user.id;
    $[129] = props.user.name;
    $[130] = props.user.username;
    $[131] = t34;
  } else {
    t34 = $[131];
  }
  let t35;
  if ($[132] !== t28 || $[133] !== t29 || $[134] !== t30 || $[135] !== t31 || $[136] !== t32 || $[137] !== t33 || $[138] !== t34) {
    t35 = <div className="ml-1 flex items-center justify-between gap-2 rounded-md bg-background-secondary py-6 pl-3 pr-3 shadow-sm hover:bg-background-light">{t28}{t29}{t30}{t31}{t32}{t33}{t34}</div>;
    $[132] = t28;
    $[133] = t29;
    $[134] = t30;
    $[135] = t31;
    $[136] = t32;
    $[137] = t33;
    $[138] = t34;
    $[139] = t35;
  } else {
    t35 = $[139];
  }
  let t36;
  if ($[140] !== privateConfirmDialog || $[141] !== t35) {
    t36 = <>{privateConfirmDialog}{t35}</>;
    $[140] = privateConfirmDialog;
    $[141] = t35;
    $[142] = t36;
  } else {
    t36 = $[142];
  }
  return t36;
};
export default InlineProfileCard;
function _temp(r) {
  return {
    ...r,
    following: true
  };
}
function _temp2(r_0) {
  return {
    ...r_0,
    following: false
  };
}